/**
 * Съвпадения за проверка — procurement patterns worth a second look.
 *
 * Every column is one published observation, shown separately. There is no
 * score and no verdict: the list is ordered by the money riding on awards
 * that carry at least one signal, which is a quantity anyone can check,
 * rather than by a weighting that would encode a judgement the reader
 * never agreed to. A company is here because of what the sources say about
 * it, and each figure links back to the records it came from.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { graphql } from '../gql';
import { request } from '../lib/graphql';
import { DataTable, Pager } from '../components/DataTable';
import { FacetSidebar } from '../components/FacetSidebar';
import { signalFacetSet } from '../facets/signals';
import { useScopedParams } from '../lib/router';
import {
  Amount, ErrorState, Link, Loading, PageHead, Refreshing, SectionHead, Stat,
} from '../components/ui';
import { compact, dateTime, num, truncate } from '../lib/format';
import { downloadXlsx, sheetOf, stampedFilename, type ExportColumn } from '../lib/xlsx';
import type { ResultOf } from '@graphql-typed-document-node/core';

const PAGE_SIZE = 25;

const SUMMARY = graphql(`
  query SignalSummary {
    signalSummary { value count }
    signalsComputedAt
  }
`);

const YEARS = graphql(`
  query SignalYears { signalYears }
`);

const LEADS = graphql(`
  query SignalLeads(
    $limit: Int!, $offset: Int!, $year: Int!, $search: String!, $flags: [String!]!,
    $minShare: Float!, $minFlaggedValue: Float!, $sort: String!, $dir: String!
  ) {
    signalLeadCount(
      year: $year, search: $search, flags: $flags,
      minShare: $minShare, minFlaggedValue: $minFlaggedValue
    )
    signalLeads(
      limit: $limit, offset: $offset, year: $year, search: $search, flags: $flags,
      minShare: $minShare, minFlaggedValue: $minFlaggedValue, sort: $sort, dir: $dir
    ) {
      uic
      name
      awards
      awardValue
      singleBid
      singleBidValue
      directAward
      directAwardValue
      subcontracted
      sharedAwards
      buyers
      topBuyerKey
      topBuyerName
      topBuyerShare
      topBuyerValue
      topBuyerTotal
      mandateAwards
      mandateValue
      mandateOfficials
      cobidders
      councilInterest
      councilInterestValue
      councilInterestNamesakes
      holdingsUndated
      topPartyKey
      topPartyName
      topPartyShare
      topPartyValue
      councilPartyKey
      councilPartyName
      councilPartyShare
      councilMajorityValue
      flaggedAwards
      flaggedValue
    }
  }
`);

/* Which sidebar selection each headline figure stands for. `profiled` and
   `municipalBuyer` have none: they describe the set the page is drawn
   from, not a subset of it, so they stay plain. */
const CARD_FILTERS: Record<string, string> = {
  singleBid: 'g.flags=singleBid',
  directAward: 'g.flags=directAward',
  subcontracted: 'g.flags=subcontracted',
  cobidders: 'g.flags=cobidders',
  soleSupplier: 'g.flags=soleSupplier',
  concentrated: 'g.share=0.5',
  onePartyWide: 'g.party=onePartyMayors',
  onePartyCouncils: 'g.party=onePartyCouncils',
  cohabitation: 'g.party=cohabitation',
};

const SUMMARY_LABELS: Record<string, string> = {
  profiled: 'Профилирани фирми',
  singleBid: 'С единствена оферта',
  directAward: 'С директно възлагане',
  subcontracted: 'С подизпълнители',
  concentrated: 'Над 50% от възложител',
  soleSupplier: 'Единствен изпълнител',
  mandate: 'Възложено от свой кмет',
  cobidders: 'Споделят лице и възложител',
  onePartyWide: 'Под кметове на една партия (3+ възложителя)',
  onePartyCouncils: 'Под съвети на една партия (3+ възложителя)',
  cohabitation: 'Кмет и съвет от различни партии',
};

/**
 * One fired signal, sized to what it says.
 *
 * A count on its own is not readable — 7 of what? — and a column header
 * three cells away stops answering that the moment the table wraps or a
 * phone stacks it. So each chip carries its own label, and a signal that
 * did not fire is simply absent: a row of dashes reads as a measurement
 * where there was none.
 *
 * `strong` marks the ones the source publishes outright (bid counts,
 * procedure names) as against the ones we count ourselves.
 */
function Chip({ label, value, sub, strong }: Readonly<{
  label: string; value: string; sub?: string; strong?: boolean;
}>) {
  return (
    <span className={strong ? 'sig-chip sig-chip-strong' : 'sig-chip'}>
      <span className="sig-chip-label">{label}</span>
      <span className="sig-chip-value">{value}</span>
      {sub && <span className="sig-chip-sub">{sub}</span>}
    </span>
  );
}

const share = (value: number | null | undefined) =>
  (value == null ? '—' : `${(value * 100).toFixed(1)}%`);

/* What each column counts, what the source publishes, and where it stops
   short of being evidence. Written out because a number with no definition
   invites the reader to supply their own, and the difference between "won
   without competition" and "was the only bidder" decides what the figure
   is worth. */
const DEFINITIONS: [string, string][] = [
  ['Поръчки',
    'Всички възложени поръчки на фирмата и общата им стойност. Поръчка с няколко '
    + 'изпълнителя се брои изцяло за всеки от тях, защото ЦАИС ЕОП публикува една '
    + 'стойност за договора и не я разпределя между изпълнителите — колоната показва '
    + 'и колко от поръчките са споделени.'],
  ['Единствена оферта',
    'Поръчки, за които възложителят е публикувал точно една получена оферта. Това е '
    + 'най-силният от признаците тук, защото броят оферти е публикуван, а не изчислен. '
    + 'Не е доказателство: специализирана дейност привлича един кандидат по естествени '
    + 'причини.'],
  ['Директно възлагане',
    'Процедури, проведени без предварително обявление — „Договаряне без предварително '
    + 'обявление“, „Пряко договаряне“ и сродните им. Законни и понякога наложителни; '
    + 'въпросът е за възложител, който ги ползва редовно.'],
  ['Концентрация — възложител',
    'Каква част от разходите на един възложител достигат до тази фирма, с общата сума '
    + 'на възложителя до нея — голям дял от малък бюджет не е същата находка като голям '
    + 'дял от голям. Изчислява се само за общините: министерствата се съхраняват като '
    + 'име върху поръчката, не като отделен обект.'],
  ['Възложено от свой кмет',
    'Поръчки, възложени от община, докато действащ кмет или кмет на кметство е бил '
    + 'собственик или управител на спечелилата фирма. Мандатите и дяловете носят '
    + 'начална и крайна дата, така че въпросът е кой е бил на власт към датата на '
    + 'възлагането, не кой държи фирмата днес. Общинската администрация е изключена — '
    + 'всеки кмет е представител на своята администрация и това не е конфликт. '
    + 'Към момента признакът не се задейства за нито една фирма и това не значи, че '
    + 'такива случаи няма. Кметовете идват от протоколите на ЦИК, които не публикуват '
    + 'ЕГН, а собствениците и управителите — от Търговския регистър, който публикува '
    + 'идентификатора вече хеширан. Двата записа за едно и също лице стоят под различни '
    + 'ключове и не се засичат — освен когато фирмата е самата общинска администрация, '
    + 'а точно този случай е изключен нарочно. Проверката се прави всеки час и ще '
    + 'проработи от само себе си, ако свързването между двата регистъра стане възможно.'],
  ['Концентрация — партия на кмета',
    'За всяка поръчка — партията, издигнала кмета, който е бил на власт към датата на '
    + 'възлагането, а не днес. Това е партията на изпълнителната власт, не на тази, '
    + 'която приема бюджета: бюджетът и разходите се гласуват от общинския съвет, а в '
    + '161 от 265 общини партията на кмета няма мнозинство в него — в 52 съветът се '
    + 'води от друга партия. Затова колоната казва под кой кмет е възложена работата, '
    + 'а не под чие управление. Съставът на съвета е показан на страницата на общината. '
    + 'Чете се задължително заедно с броя възложители: '
    + 'фирма с един общински възложител е автоматично 100% под неговата партия и това не '
    + 'значи нищо. Не е и базова честота — ГЕРБ управлява голяма част от общините, така че '
    + 'за повечето фирми водещата партия ще е ГЕРБ без от това да следва каквото и да е. '
    + 'Местните коалиции са отделни партии тук („ПП ГЕРБ“ и „МК ГЕРБ (СДС)“ не са едно и '
    + 'също), затова фирма, разпръсната по коалициите на една партия, ще покаже по-нисък дял.'],
  ['С подизпълнител',
    'Поръчки, за които възложителят е публикувал, че част от работата ще се изпълни от '
    + 'подизпълнител. Обичайна практика в строителството; тук е показана, защото верига '
    + 'от подизпълнители е и начинът, по който работа стига до фирма, която не би могла '
    + 'да я спечели пряко.'],
  ['Свързани фирми',
    'Други фирми, които споделят с тази и лице (собственик или управител), и възложител. '
    + 'Общинските администрации не се броят за свързани фирми.'],
  ['Стойност с признак',
    'Стойността на поръчките, върху които пада поне един от горните признаци — '
    + 'по нея е подредена таблицата. Умишлено не е претеглена оценка: тежести, които '
    + 'никой не е калибрирал, поставят преценката на автора там, където трябва да е '
    + 'преценката на читателя.'],
];

type SignalLead = ResultOf<typeof LEADS>['signalLeads'][number];

/* Every stored component, not just what the table has room for: the file
   is where a reader checks the reasoning, so nothing the signals job
   computed is held back. Shares stay raw ratios with a percent display
   format — 0.87345 shown as 87,3%, not baked into a string. */
const LEAD_EXPORT: readonly ExportColumn<SignalLead>[] = [
  { header: 'ЕИК', value: (row) => row.uic },
  { header: 'Фирма', value: (row) => row.name },
  { header: 'Поръчки', value: (row) => row.awards },
  { header: 'Стойност на поръчките (лв.)', format: 'money', value: (row) => row.awardValue },
  { header: 'Споделени поръчки', value: (row) => row.sharedAwards },
  { header: 'Единствена оферта', value: (row) => row.singleBid },
  { header: 'Единствена оферта — стойност (лв.)', format: 'money', value: (row) => row.singleBidValue },
  { header: 'Директно възлагане', value: (row) => row.directAward },
  { header: 'Директно възлагане — стойност (лв.)', format: 'money', value: (row) => row.directAwardValue },
  { header: 'С подизпълнители', value: (row) => row.subcontracted },
  { header: 'Общински възложители', value: (row) => row.buyers },
  { header: 'Най-голям възложител', value: (row) => row.topBuyerName },
  { header: 'Дял от възложителя', format: 'percent', value: (row) => row.topBuyerShare },
  { header: 'От възложителя (лв.)', format: 'money', value: (row) => row.topBuyerValue },
  { header: 'Общо на възложителя (лв.)', format: 'money', value: (row) => row.topBuyerTotal },
  { header: 'Възложено от свой кмет', value: (row) => row.mandateAwards },
  { header: 'От свой кмет — стойност (лв.)', format: 'money', value: (row) => row.mandateValue },
  { header: 'Длъжностни лица', value: (row) => row.mandateOfficials },
  { header: 'Дялове без начална дата', value: (row) => row.holdingsUndated },
  { header: 'Свързани фирми', value: (row) => row.cobidders },
  { header: 'Партия на кмета', value: (row) => row.topPartyName },
  { header: 'Дял под кметове от нея', format: 'percent', value: (row) => row.topPartyShare },
  { header: 'Водеща партия в съвета', value: (row) => row.councilPartyName },
  { header: 'Дял под съвети, водени от нея', format: 'percent',
    value: (row) => row.councilPartyShare },
  { header: 'От тях с мнозинство в съвета (лв.)', format: 'money',
    value: (row) => row.councilMajorityValue },
  { header: 'Под партията (лв.)', format: 'money', value: (row) => row.topPartyValue },
  { header: 'Поръчки с признак', value: (row) => row.flaggedAwards },
  { header: 'Стойност с признак (лв.)', format: 'money', value: (row) => row.flaggedValue },
];

/* The sort orders the resolver understands. Offered as a list rather
   than derived from the columns, because the columns no longer map one
   to one onto figures — five signals live in one cell now, and a reader
   still wants to rank by any of them. */
const SORTS = [
  { key: 'flaggedValue', label: 'Стойност с признак' },
  { key: 'awardValue', label: 'Стойност на поръчките' },
  { key: 'awards', label: 'Брой поръчки' },
  { key: 'singleBid', label: 'Единствена оферта' },
  { key: 'directAward', label: 'Директно възлагане' },
  { key: 'concentration', label: 'Дял от възложител' },
] as const;

export function SignalsPage() {
  /* Filters live in the URL, as they do on every other list here: a
     narrowed lead list is a finding, and a finding that cannot be sent to
     a colleague is half a finding. */
  const params = useScopedParams('g.');

  const summary = useQuery({
    queryKey: ['signal-summary'],
    queryFn: () => request(SUMMARY),
  });
  const yearsQuery = useQuery({
    queryKey: ['signal-years'],
    queryFn: () => request(YEARS),
  });
  const years = yearsQuery.data?.signalYears ?? [];

  const facets = useMemo(() => signalFacetSet(years), [years]);
  const state = useMemo(
    () => facets.fromParams(params.get, params.getAll), [facets, params]);
  const filter = useMemo(() => facets.toFilter(state), [facets, state]);

  const page = Math.max(0, (Number(params.get('page')) || 1) - 1);
  const sort = params.get('sort') ?? 'flaggedValue';
  const dir = params.get('dir') === 'ASC' ? 'ASC' : 'DESC';

  /* The box keeps its own value so typing is not throttled by the round
     trip; the committed term lives in the URL. */
  const [draft, setDraft] = useState<string>(() => state.search);
  useEffect(() => { setDraft(state.search); }, [state.search]);

  /* On a phone the panel is a bottom sheet behind a „Филтри“ button; on
     wide screens the same markup is the always-visible sidebar and this
     state has no effect. */
  const [filtersOpen, setFiltersOpen] = useState(false);
  useEffect(() => {
    if (!filtersOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFiltersOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filtersOpen]);

  const leads = useQuery({
    queryKey: ['signal-leads', filter, sort, dir, page],
    queryFn: () => request(LEADS, {
      ...filter, limit: PAGE_SIZE, offset: page * PAGE_SIZE, sort, dir,
    }),
    placeholderData: keepPreviousData,
  });

  const counts = Object.fromEntries(
    (summary.data?.signalSummary ?? []).map((row) => [row.value, row.count]));
  const profiled = counts['profiled'] ?? 0;
  /* The number of rows these filters leave, which is not the number of
     profiled companies: the list only holds those that trip something.
     The pager was given the larger figure and offered pages that were
     not there. */
  const total = leads.data?.signalLeadCount ?? 0;
  /* Whether the mandate check is capable of firing at all — see the note
     below the table. Read from the data rather than hard-coded off, so
     the chip, the filter and the figure all come back on their own if
     the identity gap is ever closed. */
  const mandateWorks = (counts['mandate'] ?? 0) > 0;
  const year = filter.year;

  const apply = useCallback((next: typeof state) => {
    params.set({ ...facets.toParams(next), page: null });
  }, [facets, params]);

  const onToggleList = useCallback((key: string, value: string) => {
    const list = Array.isArray(state[key]) ? (state[key] as string[]) : [];
    apply({
      ...state,
      [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value],
    });
  }, [apply, state]);

  const onSetValue = useCallback((key: string, value: string | boolean) => {
    apply({ ...state, [key]: value });
  }, [apply, state]);

  const commitSearch = useCallback((term: string) => {
    apply({ ...state, search: term });
  }, [apply, state]);

  /* The whole filtered lead list, walked to the end — with the column
     definitions as a sheet of their own. This page's premise is that a
     number with no definition invites the reader to supply their own, and
     that goes double for a spreadsheet forwarded without the page. */
  const [exporting, setExporting] = useState<number | null>(null);
  /* A failed export used to re-enable the button and say nothing,
     which reads as a file that silently did not download. */
  const [exportError, setExportError] = useState<string | null>(null);
  const exportAll = async () => {
    setExporting(0);
    setExportError(null);
    try {
      const all: SignalLead[] = [];
      const batch = 200;
      for (let offset = 0; offset < 10_000; offset += batch) {
        const data = await request(LEADS, {
          ...filter, limit: batch, offset, sort, dir,
        });
        all.push(...data.signalLeads);
        setExporting(all.length);
        if (data.signalLeads.length < batch) break;
      }
      downloadXlsx(stampedFilename('признаци'), [
        sheetOf('Фирми', LEAD_EXPORT, all),
        {
          name: 'Определения',
          header: ['Колона', 'Какво показва'],
          rows: DEFINITIONS,
        },
        {
          name: 'Справка',
          header: ['Поле', 'Стойност'],
          rows: [
            ['Източник', 'Алфа-Омега — от бюджета до получателя'],
            ['Изтеглено на', new Date().toISOString()],
            ['Изчислено на', summary.data?.signalsComputedAt ?? '—'],
            ['Година на възлагане', year ? String(year) : 'всички'],
            ['Признаци', state.flags.length ? state.flags.join(', ') : 'без ограничение'],
            ['Търсене', state.search || 'няма'],
            ['Дял от възложител',
              filter.minShare > 0 ? `над ${filter.minShare * 100}%` : 'без ограничение'],
            ['Стойност с признак',
              filter.minFlaggedValue > 0
                ? `над ${filter.minFlaggedValue.toLocaleString('bg-BG')} лв`
                : 'без ограничение'],
            ['Редове', all.length],
            ['Общо в извадката', total],
            ['Подредба', `${sort} ${dir}`],
            ['Бележка', 'Признак не е доказателство — вижте листа „Определения“.'],
          ],
        },
      ]);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <PageHead
        eyebrow="Съвпадения за проверка"
        title="Модели в обществените поръчки"
        lede={'Всеки признак е едно публикувано наблюдение, показано отделно. Няма обобщена '
          + 'оценка и няма заключение — подредбата е по стойността на поръчките, върху които '
          + 'пада поне един признак. Признак не е доказателство: единствената оферта може да '
          + 'е следствие от специализирана дейност, а голям дял от малък бюджет не е същото '
          + 'като голям дял от голям. Филтрите отстрани стесняват списъка, а изнесеният файл '
          + 'следва същите филтри.'}
      />

      {summary.data?.signalsComputedAt && (
        <p className="colophon-note" style={{ marginTop: '-1.2rem' }}>
          Изчислено на {dateTime(summary.data.signalsComputedAt)} ч. Признаците се преизчисляват
          всеки час, след приключване на текущото зареждане на данни, и се преизчисляват
          изцяло — признак, който вече не е в сила, изчезва, вместо да остане от предишен
          запис.
        </p>
      )}

      {summary.isError && <ErrorState message={(summary.error).message} />}
      {summary.data && (
        <div className="grid grid-4">
          {Object.entries(SUMMARY_LABELS).map(([key, label]) => (
            <Stat
              key={key}
              /* Every card that names a set the sidebar can select links to
                 it. A figure the reader cannot open is a figure they have
                 to take on trust, and this page's whole argument is that
                 they should not have to. */
              href={CARD_FILTERS[key]
                ? `#/signals?${CARD_FILTERS[key]}`
                : undefined}
              /* A zero we cannot stand behind is shown as a gap. The
                 mandate check needs the same person to appear in two
                 registers that key people differently, and it currently
                 never does — printing "0" there would report a clean
                 result from a test that never ran. */
              value={key === 'mandate' && !mandateWorks ? '—' : num(counts[key] ?? 0)}
              label={label}
            />
          ))}
        </div>
      )}

      {leads.isError && <ErrorState message={(leads.error).message} />}

      <section className="block">
        <SectionHead
          title="Фирми"
          aside={leads.isPending
            ? 'зареждане…'
            : `${num(total)} от ${num(profiled)} профилирани`
              + (year ? ` · възложени през ${year}` : '')}
        />
        <div className="list-shell">
          <div className={filtersOpen ? 'facet-drawer is-open' : 'facet-drawer'}>
            <div className="facet-scrim" aria-hidden="true"
              onClick={() => setFiltersOpen(false)} />
            <FacetSidebar
              facets={facets}
              state={state}
              /* No counts. The list is already the narrow set — companies
                 that trip at least one signal — and a figure beside every
                 box would cost an aggregation over the whole graph on each
                 keystroke to say what the row count above already says. */
              counts={{}}
              onToggleList={onToggleList}
              onSetValue={onSetValue}
              onReset={() => { setDraft(''); params.clear(); }}
              onClose={() => setFiltersOpen(false)}
            />
          </div>
          <div>
            <div className="toolbar">
              <input
                type="search"
                placeholder="Търси по име или ЕИК…"
                enterKeyHint="search"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') commitSearch(draft); }}
                onBlur={() => { if (draft !== state.search) commitSearch(draft); }}
              />
              <button type="button" className="btn" onClick={() => commitSearch(draft)}>
                Търси
              </button>
              <button
                type="button"
                className="btn facet-toggle"
                aria-expanded={filtersOpen}
                onClick={() => setFiltersOpen(true)}
              >
                Филтри{facets.activeCount(state) > 0 ? ` · ${facets.activeCount(state)}` : ''}
              </button>
              <label className="sort-mobile">
                <span className="sr-only">Подредба</span>
                <select
                  value={`${sort}:${dir}`}
                  onChange={(event) => {
                    const [key, next] = event.target.value.split(':');
                    params.set({ sort: key ?? null, dir: next ?? null, page: null });
                  }}
                >
                  {SORTS.flatMap((option) => [
                    <option key={`${option.key}:DESC`} value={`${option.key}:DESC`}>
                      {option.label} ↓
                    </option>,
                    <option key={`${option.key}:ASC`} value={`${option.key}:ASC`}>
                      {option.label} ↑
                    </option>,
                  ])}
                </select>
              </label>
              <button
                type="button"
                className="btn"
                onClick={() => { void exportAll(); }}
                disabled={exporting != null || leads.isPending}
                title="Изтегля всички фирми при избраните филтри, с определенията на колоните"
              >
                {exporting != null
                  ? `Изтегляне… ${exporting.toLocaleString('bg-BG')}`
                  : 'Експорт XLSX'}
              </button>
              {exportError && (
                <span className="muted">Експортът не сполучи: {exportError}</span>
              )}
              {!mandateWorks && (
                /* The one filter the sidebar does not offer, and why. Left
                   as a statement rather than a disabled box: a control that
                   cannot be used is still a control the eye stops on. */
                <span className="facet-note">
                    признакът „възложено от свой кмет“ не може да се провери — вижте бележката
                </span>
              )}
            </div>

            {leads.isPending ? <Loading rows={8} /> : (
              <Refreshing busy={leads.isFetching}>
                <DataTable<SignalLead>
                  rows={leads.data?.signalLeads ?? []}
                  rowKey={(row) => row.uic}
                  sort={{ key: sort, dir }}
                  onSort={(key) => params.set({
                    sort: key,
                    dir: sort === key && dir === 'DESC' ? 'ASC' : 'DESC',
                    page: null,
                  })}
                  empty="Няма фирми, отговарящи на тези условия."
                columns={[
                  {
                    key: 'name',
                    label: 'Фирма',
                    render: (row) => (
                      <div>
                        <Link href={`#/companies/${row.uic}`}>{row.name ?? row.uic}</Link>
                        <div className="uic mono">
                          {row.uic} · {row.awards} поръчки · {compact(row.awardValue)} лв
                          {row.sharedAwards > 0 && ` · ${row.sharedAwards} споделени`}
                        </div>
                      </div>
                    ),
                  },
                  {
                    /* Five columns became one. Each signal was a column of its
                       own, so a company that trips two of them paid for the
                       width of all five, and the two furthest right — the ones
                       naming a party and a value — were off the screen before
                       the table had said anything. As chips they take the width
                       they need, wrap when there is not enough, and a signal
                       that does not apply takes none at all instead of a dash. */
                    key: 'signals',
                    label: 'Признаци',
                    render: (row) => (
                      <div className="sig-chips">
                        {/* First, and stronger, because the source publishes
                            these rather than us deriving them. */}
                        {row.singleBid > 0 && (
                          <Chip strong label="единствена оферта"
                            value={num(row.singleBid)} sub={`${compact(row.singleBidValue)} лв`} />
                        )}
                        {row.directAward > 0 && (
                          <Chip strong label="директно възлагане"
                            value={num(row.directAward)} sub={`${compact(row.directAwardValue)} лв`} />
                        )}
                        {row.mandateAwards > 0 && (
                          <Chip strong label="от свой кмет"
                            value={num(row.mandateAwards)}
                            sub={`${compact(row.mandateValue)} лв · ${row.mandateOfficials} лице`
                              + (row.holdingsUndated > 0 ? ' · дата на дела неизвестна' : '')} />
                        )}
                        {row.subcontracted > 0 && (
                          <Chip label="с подизпълнител" value={num(row.subcontracted)} />
                        )}
                        {row.cobidders > 0 && (
                          <Chip label="свързани фирми" value={num(row.cobidders)} />
                        )}
                        {/* Marked strong only where the name belongs to one
                            businessperson in the whole register; where
                            several share it the chip is quiet, because the
                            match is. */}
                        {row.councilInterest > 0 && (
                          <Chip
                            strong={row.councilInterestNamesakes === 1}
                            label="име на съветник"
                            value={num(row.councilInterest)}
                            sub={`${compact(row.councilInterestValue)} лв`
                              + (row.councilInterestNamesakes > 1
                                ? ` · ${row.councilInterestNamesakes} лица с това име`
                                : '')}
                          />
                        )}
                        {row.singleBid === 0 && row.directAward === 0 && row.subcontracted === 0
                          && row.cobidders === 0 && row.councilInterest === 0
                          && <span className="muted">—</span>}
                      </div>
                    ),
                  },
                  {
                    /* Not `num`: that class sets white-space: nowrap, which is
                       why a party like „МК ГЕРБ (СДС) — Пловдив" used to push
                       the value column past the right edge rather than wrap.
                       The share stays right-aligned in its own track. */
                    key: 'concentration',
                    /* Named for the period it covers, because with a year
                       selected every other figure in the row is that year's
                       and this one is not. A share of one year of a
                       municipal budget turns on whether a large project
                       happened to be tendered that year, so it is left
                       whole — but a column that quietly means something
                       different from its neighbours has to say so. */
                    label: year ? 'Концентрация · целия период' : 'Концентрация',
                    render: (row) => (
                      <div className="sig-conc">
                      {row.topBuyerShare == null ? (
                        // COMMISSIONED_BY exists only for municipal buyers,
                        // so a company supplying ministries has no share
                        <span className="muted">няма общински възложител</span>
                      ) : (
                        <div className="sig-conc-row">
                          <span className="sig-conc-key">възложител</span>
                          <span className="sig-conc-share">{share(row.topBuyerShare)}</span>
                          <span
                            className="sig-conc-who"
                            title={`${row.topBuyerName ?? ''} — от `
                              + `${compact(row.topBuyerTotal)} лв общо`}
                          >
                            {row.topBuyerKey
                              ? (
                                <Link href={`#/municipalities/${encodeURIComponent(row.topBuyerKey)}`}>
                                  {truncate(row.topBuyerName, 24)}
                                </Link>
                              )
                              : truncate(row.topBuyerName, 24)}
                          </span>
                        </div>
                      )}
                      {/* Two answers to "under whom", because there are two
                          bodies: the mayor signs, the council authorises.
                          Shown together rather than reconciled — they differ
                          for 1,743 of 7,830 profiled companies, and the
                          difference is the finding. */}
                      {row.councilPartyShare != null && (
                        <div className="sig-conc-row">
                          <span className="sig-conc-key">съвет от</span>
                          <span className="sig-conc-share">
                            {share(row.councilPartyShare)}
                          </span>
                          <span
                            className="sig-conc-who"
                            title={`${row.councilPartyName ?? ''} — водеща група в съвета; `
                              + (row.councilMajorityValue
                                ? `${compact(row.councilMajorityValue)} лв от тях с мнозинство`
                                : 'никъде с мнозинство в съвета')}
                          >
                            {row.councilPartyKey
                              ? (
                                <Link href={`#/parties/${encodeURIComponent(row.councilPartyKey)}`}>
                                  {truncate(row.councilPartyName, 24)}
                                </Link>
                              )
                              : truncate(row.councilPartyName, 24)}
                          </span>
                        </div>
                      )}
                      {row.topPartyShare != null && (
                        <div className="sig-conc-row">
                          <span className="sig-conc-key">кмет от</span>
                          <span className="sig-conc-share">{share(row.topPartyShare)}</span>
                          <span
                            className="sig-conc-who"
                            title={`${row.topPartyName ?? ''} — партия на кмета; `
                              + `${row.buyers} общински възложителя`}
                          >
                            {row.topPartyKey
                              ? (
                                <Link href={`#/parties/${encodeURIComponent(row.topPartyKey)}`}>
                                  {truncate(row.topPartyName, 24)}
                                </Link>
                              )
                              : truncate(row.topPartyName, 24)}
                          </span>
                        </div>
                      )}
                    </div>
                  ),
                  },
                  {
                    key: 'flagged',
                    label: 'Стойност с признак',
                    num: true,
                    render: (row) => (
                      <Amount value={row.flaggedValue} />
                    ),
                  },
                ]}
                />
                <Pager
                  page={page}
                  pageSize={PAGE_SIZE}
                  total={total}
                  onGo={(next) => params.set({ page: next ? String(next + 1) : null })}
                />
              </Refreshing>
            )}
          </div>
        </div>
      </section>

      <section className="block">
        <SectionHead title="Какво показва всяка колона" aside="определения" />
        <dl className="detail-meta detail-meta-wide">
          {DEFINITIONS.map(([term, meaning]) => (
            <div key={term}><dt>{term}</dt><dd>{meaning}</dd></div>
          ))}
        </dl>
      </section>

      <p className="colophon-note">
        Поръчки, чиято публикувана стойност е точно 100 пъти прогнозната, не влизат в
        никоя сума тук: това е изпусната десетична запетая в източника, не договор.
        Публикуваните числа се пазят както са — премълчава се само нашата сравнима
        стойност, защото деление на 100 би било същата измислица с по-приятно лице.{' '}
        Делът от възложител се изчислява само върху общините — министерствата и другите
        държавни възложители се съхраняват като име върху поръчката, не като отделен обект,
        затова за {num(total - (counts['municipalBuyer'] ?? 0))} фирми той не е определен. Поръчка с
        няколко изпълнители се брои изцяло за всеки от тях, защото източникът не публикува
        разпределение — колоната „споделени“ показва колко от поръчките са такива.
      </p>

      {!mandateWorks && (
        <p className="colophon-note">
          Признакът „възложено от свой кмет“ не се задейства за нито една фирма и затова е
          показан като празен, а не като нула. Кметовете идват от ЦИК без ЕГН, а
          собствениците и управителите — от Търговския регистър с вече хеширан
          идентификатор, така че едно и също лице стои в графа под два различни ключа.
          Единственото място, където двата се събират, е общинската администрация, а кмет,
          който подписва за собствената си администрация, не е конфликт и е изключен
          нарочно. Нула тук би значела, че сме проверили и не сме намерили нищо; истината е,
          че проверката не може да се направи с това, което двата регистъра публикуват.
        </p>
      )}
    </>
  );
}
